import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TiWarning } from 'react-icons/ti';
import { useParams } from 'react-router-dom';

import { useGetBuildingsByIdQuery, useGetStatisticsVacancyQuery } from '@client/api';
import { Card, CardContent, CardHeader, CardTitle, isDefinedNumber, Text, toNumberString } from '@client/toolkit';

import { AreaWidget } from '../area-widget/area-widget.component';
import { BuildingInformationCard } from '../../buildings/building/dashboard/components/building-information-card.component';

export type CommercialItemsCardProps = {
  className?: string;
  dense?: boolean;
};

export function CommercialItemsCard({ className, dense = false }: CommercialItemsCardProps) {
  const { t: tType } = useTranslation('type');
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tGlobal } = useTranslation('global');

  const { buildingId } = useParams<{ buildingId: string }>();

  const { data: building, isFetching } = useGetBuildingsByIdQuery({ id: buildingId ?? '' }, { skip: !buildingId });

  const buildingPath = useMemo(() => building?.path, [building?.path]);

  // Fetch ALL spaces for over/under-allocated calculation (same approach as groups)
  const { data: allSpacesReport, isFetching: isFetchingAllSpaces } = useGetStatisticsVacancyQuery(
    {
      'ancestorPath.Equal': buildingPath,
    },
    { skip: !buildingPath },
  );

  const isLoading = isFetching || isFetchingAllSpaces;

  // Use building's defined area and ALL child spaces for over/under-allocated calculation
  const definedArea = building?.area?.metric;
  const childSpacesArea = allSpacesReport?.total?.area?.metric;

  // Calculate extra area if child spaces exceed the defined area
  const extraArea = useMemo(() => {
    if (!isDefinedNumber(definedArea) || !isDefinedNumber(childSpacesArea)) return undefined;
    const extra = childSpacesArea - definedArea;
    return extra > 0 ? extra : undefined;
  }, [definedArea, childSpacesArea]);

  // Calculate unused area if defined area exceeds child spaces
  const unusedArea = useMemo(() => {
    if (!isDefinedNumber(definedArea) || !isDefinedNumber(childSpacesArea)) return undefined;
    const unused = definedArea - childSpacesArea;
    return unused > 0 ? unused : undefined;
  }, [definedArea, childSpacesArea]);

  const isCommercialAreasExceeded = isDefinedNumber(extraArea) && extraArea > 0;
  const isCommercialAreasUnderAllocated = isDefinedNumber(unusedArea) && unusedArea > 0;

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex-1">
          <CardTitle className="flex-1" variant="dense">
            {tGlobal('entity.commercialSpace', { context: 'plural' })}
          </CardTitle>
          {!isLoading && isCommercialAreasExceeded && (
            <span className="text-brand-primary text-xs block mt-2 -mb-1">
              <TiWarning className="text-lg w-4 h-4 -mt-1 inline-block mr-1" />
              <Text loading={!ready}>{t('building.totalAreaExceeded')}</Text>
            </span>
          )}
          {!isLoading && isCommercialAreasUnderAllocated && (
            <span className="text-[#a1de6c] text-xs block mt-2 -mb-1">
              <TiWarning className="text-lg w-4 h-4 -mt-1 inline-block mr-1" />
              <Text loading={!ready}>{t('building.totalAreaUnderAllocated')}</Text>
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
          <AreaWidget
            label={tGlobal('total')}
            total={
              <span className="inline-flex items-baseline gap-1 flex-wrap">
                <Text
                  className="text-white whitespace-nowrap"
                  loading={isFetching}
                  isEmpty={
                    !isDefinedNumber(
                      building?.commercialSpaces?.defined?.metric ?? building?.commercialSpaces?.total?.area?.metric,
                    )
                  }
                >
                  {toNumberString(
                    building?.commercialSpaces?.defined?.metric ??
                      building?.commercialSpaces?.total?.area?.metric ??
                      undefined,
                    undefined,
                    {
                      unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                    },
                  )}
                </Text>
{isCommercialAreasExceeded && (
                  <span
                    className="text-brand-primary font-medium whitespace-nowrap"
                    style={{ fontSize: '0.65rem' }}
                  >
                    (+{' '}
                    {toNumberString(extraArea, undefined, {
                      unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                    })}
                    )
                  </span>
                )}
                {isCommercialAreasUnderAllocated && (
                  <span
                    className="text-[#a1de6c] font-medium whitespace-nowrap"
                    style={{ fontSize: '0.65rem' }}
                  >
                    (-{' '}
                    {toNumberString(unusedArea, undefined, {
                      unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                    })}
                    )
                  </span>
                )}
              </span>
            }
            /* primaryLabel={tGlobal('availableArea')}
            primary={
              <Text
                loading={isFetching}
                isEmpty={!isDefinedNumber(building?.commercialSpaces?.available?.area?.metric)}
                className={classNames(isCommercialAreasExceeded && 'text-brand-primary')}
              >
                {toNumberString(building?.commercialSpaces?.available?.area?.metric, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
                {isCommercialAreasExceeded && <TiWarning className="text-lg ml-1 -mr-6 w-5 h-5 -mt-1 inline-block" />}
              </Text>
            }
            secondaryLabel={tGlobal('unavailableArea')}
            secondary={
              <Text
                loading={isFetching}
                isEmpty={!isDefinedNumber(building?.commercialSpaces?.unavailable?.area?.metric)}
              >
                {toNumberString(building?.commercialSpaces?.unavailable?.area?.metric, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            } */
            dense={dense}
          />

          <AreaWidget
            label={tGlobal('vacant')}
            subtitle={tType('SpaceDiscriminator.CommercialSpace').toLowerCase()}
            total={
              <Text
                className="text-white"
                loading={isFetching}
                isEmpty={!isDefinedNumber(building?.commercialSpaces?.vacant?.area?.metric)}
              >
                {toNumberString(building?.commercialSpaces?.vacant?.area?.metric ?? undefined, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            }
            /* primaryLabel={tGlobal('vacantSpaceArea')}
            primary={
              <Text loading={isFetching} isEmpty={!isDefinedNumber(building?.commercialSpaces?.vacant?.area?.metric)}>
                {toNumberString(building?.commercialSpaces?.vacant?.area?.metric, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            }
            secondaryLabel={tGlobal('unusedArea')}
            secondary={
              <Text loading={isFetching} isEmpty={!isDefinedNumber(building?.commercialSpaces?.unused?.metric)}>
                {toNumberString(building?.commercialSpaces?.unused?.metric, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            } */
            dense={dense}
          />

          <AreaWidget
            label={tGlobal('rented')}
            subtitle={tType('SpaceDiscriminator.CommercialSpace').toLowerCase()}
            total={
              <Text
                className="text-white"
                loading={isFetching}
                isEmpty={!isDefinedNumber(building?.commercialSpaces?.rented?.area?.metric)}
              >
                {toNumberString(building?.commercialSpaces?.rented?.area?.metric ?? undefined, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            }
            /* primaryLabel={tGlobal('rentedSpace')}
            primary={
              <Text loading={isFetching} isEmpty={!isDefinedNumber(building?.commercialSpaces?.rented?.area?.metric)}>
                {toNumberString(building?.commercialSpaces?.rented?.area?.metric, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            }
            secondaryLabel={tGlobal('notForRent')}
            secondary={
              <Text
                loading={isFetching}
                isEmpty={!isDefinedNumber(building?.commercialSpaces?.nonRentable?.area?.metric)}
              >
                {toNumberString(building?.commercialSpaces?.nonRentable?.area?.metric, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            } */
            dense={dense}
          />

          <AreaWidget
            label={tGlobal('commonAreaTotal')}
            total={
              <Text
                className="text-white"
                loading={isFetching}
                isEmpty={!isDefinedNumber(building?.commonAreaTotal?.area?.metric)}
              >
                {toNumberString(building?.commonAreaTotal?.area?.metric ?? undefined, undefined, {
                  unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                })}
              </Text>
            }
            dense={dense}
          />
        </div>

        <BuildingInformationCard building={building} isFetching={isFetching} variant="compact" />
      </CardContent>
    </Card>
  );
}
